import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyContext } from '../../../../../../../context/CompanyContext';
import { ProductExportConfigurationContext } from '../../../../../../../context/ProductExportConfigurationContext';
import {
  ProductExportConfiguration,
  ProductExportConfigurationField,
} from '../../../../../../../types/product_export_configuration/productExportConfiguration';
import ProductExportConfigurationForm, {
  DraggableProductExportFieldIdentifier,
} from '../Common/ProductExportConfigurationForm';
import { TranslationLanguage } from '../../../../../../../types/translation/translation';
import { ISO_LanguageCode } from '../../../../../../../types/translation/localization.utils';
import { BackButton, ButtonVariant, CreateButton } from '../../../../../../components/Button';
import { Routing } from '../../../../../../../util/routes.util';
import { useRequest } from '../../../../../../../requests/common/requestHook';
import { ProductExportConfigurationRequest } from '../../../../../../../requests/productExportConfiguration.request';
import { DraggableItemData } from '../../../../../Common/DraggableItem';
import { ProductExportConfigurationInput } from '../../../../../../../types/product_export_configuration';

interface CreateProductExportConfigurationPaneProps {}

export default function CreateProductExportConfigurationPane({}: CreateProductExportConfigurationPaneProps) {
  const navigate = useNavigate();
  const { currentCompany } = useContext(CompanyContext);
  const { productExportConfigurations, setProductExportConfigurations, productExportConfigurationsLoading } =
    useContext(ProductExportConfigurationContext);

  const [input, setInput] = useState<ProductExportConfigurationInput>(
    new ProductExportConfigurationInput(
      ProductExportConfiguration.default(
        currentCompany.id,
        (currentCompany.settings.language || ISO_LanguageCode.EN) as TranslationLanguage,
      ),
    ),
  );

  const [create, { loading }] = useRequest(ProductExportConfigurationRequest.create);

  const handleCreate = async (
    configuration: ProductExportConfigurationInput,
    items: DraggableProductExportFieldIdentifier[],
  ) => {
    configuration.mapping = DraggableItemData.toMap<ProductExportConfigurationField>(items);

    const result = await create({ data: [new ProductExportConfigurationInput(configuration).forCreate()] });
    if (!result) return;

    const config = result[0];
    productExportConfigurations.set(config.id, new ProductExportConfiguration(config));
    setProductExportConfigurations(new Map(productExportConfigurations));
    navigate(Routing.stock.products.settings.report.overview());
  };

  const footer = (configuration: ProductExportConfigurationInput, items: DraggableProductExportFieldIdentifier[]) => {
    return (
      <div className='flex justify-end'>
        <div className='flex items-center gap-2'>
          <BackButton
            disabled={loading}
            variant={ButtonVariant.secondary}
            onClick={() => navigate(Routing.stock.products.settings.report.overview())}
          />
          <CreateButton
            loading={loading}
            onClick={() => handleCreate(configuration, items)}
            disabled={!configuration.filename || !configuration.name}
          />
        </div>
      </div>
    );
  };

  return (
    <ProductExportConfigurationForm
      input={input}
      setInput={setInput}
      footer={footer}
      loading={productExportConfigurationsLoading}
    />
  );
}
